import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { useClientIntakeModal } from './ModalProvider';

export const Header = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const { openClientIntake } = useClientIntakeModal();

  const navigation = [
    { name: 'Home', path: '/' },
    { name: 'About Us', path: '/about' },
    { name: 'Services', path: '/services' },
    { name: 'Industries', path: '/industries' },
    { name: 'Employers', path: '/employers' },
    { name: 'Candidates', path: '/candidates' },
    { name: 'Contact', path: '/contact' }
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 backdrop-blur-md border-b ${
        isHomePage
          ? 'bg-[#0A1F44]/80 border-white/10'
          : 'bg-white/95 border-gray-200'
      }`}
    >
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo — hidden on homepage */}
          {!isHomePage && (
            <Link to="/" className="flex items-center group flex-shrink-0">
              <div className="bg-gradient-to-br from-[#0A1F44] to-[#0d2b5c] px-3 py-1.5 rounded-lg">
                <img
                  src="https://customer-assets.emergentagent.com/job_talent-delivery-2/artifacts/n21hqg7x_1000003920%20%281%29.png"
                  alt="Omnia Paratus"
                  className="h-10 w-auto transition-transform duration-300 group-hover:scale-105"
                />
              </div>
            </Link>
          )}

          {/* Desktop Navigation */}
          <div
            data-testid="desktop-nav"
            className={`hidden lg:flex items-center gap-8 ${
              isHomePage ? 'flex-1 justify-center' : ''
            }`}
          >
            {navigation.map((item) => (
              <Link
                key={item.name}
                to={item.path}
                data-testid={`nav-link-${item.name.toLowerCase()}`}
                className={`text-sm font-medium transition-colors duration-200 relative group whitespace-nowrap ${
                  isHomePage
                    ? isActive(item.path)
                      ? 'text-[#D4AF37]'
                      : 'text-white/80 hover:text-white'
                    : isActive(item.path)
                    ? 'text-[#0A1F44]'
                    : 'text-gray-600 hover:text-[#0A1F44]'
                }`}
              >
                {item.name}
                <span
                  className={`absolute -bottom-1 left-0 h-0.5 bg-[#D4AF37] transition-all duration-300 ${
                    isActive(item.path) ? 'w-full' : 'w-0 group-hover:w-full'
                  }`}
                />
              </Link>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden lg:flex items-center gap-3 flex-shrink-0">
            <Button
              asChild
              variant="outline"
              size="sm"
              className={`transition-all duration-300 ${
                isHomePage
                  ? 'border-white/40 text-white hover:bg-white hover:text-[#0A1F44]'
                  : 'border-[#0A1F44] text-[#0A1F44] hover:bg-[#0A1F44] hover:text-white'
              }`}
            >
              <Link to="/candidates">Find Jobs</Link>
            </Button>
            <Button
              size="sm"
              className="bg-[#D4AF37] text-[#0A1F44] hover:bg-[#D4AF37]/90 transition-all duration-300 shadow-md hover:shadow-lg font-semibold"
              onClick={openClientIntake}
              data-testid="header-request-talent-btn"
            >
              Request Talent
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`lg:hidden p-2 rounded-md transition-colors duration-200 ${
              isHomePage
                ? 'text-white hover:bg-white/10'
                : 'text-gray-600 hover:text-[#0A1F44] hover:bg-gray-100'
            }`}
          >
            {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div
            data-testid="mobile-menu"
            className={`lg:hidden py-4 border-t animate-in slide-in-from-top duration-300 ${
              isHomePage ? 'border-white/10' : 'border-gray-200'
            }`}
          >
            <div className="flex flex-col space-y-1">
              {navigation.map((item) => (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-2.5 text-base font-medium rounded-md transition-colors duration-200 ${
                    isHomePage
                      ? isActive(item.path)
                        ? 'bg-[#D4AF37] text-[#0A1F44]'
                        : 'text-white hover:bg-white/10'
                      : isActive(item.path)
                      ? 'bg-[#0A1F44] text-white'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
              <div className="pt-4 px-4 flex flex-col gap-3">
                <Button
                  asChild
                  variant="outline"
                  className={`w-full ${
                    isHomePage
                      ? 'border-white/40 text-white hover:bg-white hover:text-[#0A1F44]'
                      : 'border-[#0A1F44] text-[#0A1F44]'
                  }`}
                >
                  <Link to="/candidates" onClick={() => setMobileMenuOpen(false)}>
                    Find Jobs
                  </Link>
                </Button>
                <Button
                  className="w-full bg-[#D4AF37] text-[#0A1F44] hover:bg-[#D4AF37]/90 font-semibold"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    openClientIntake();
                  }}
                  data-testid="mobile-request-talent-btn"
                >
                  Request Talent
                </Button>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};
